/**
 * A password input with a show / hide toggle and, for new passwords, a live strength meter
 * against the rules the server enforces. Used by the sign-in, registration, reset and invite
 * pages and the member portal. Unstyled beyond the shared .field classes plus a few utility
 * classes that already exist in the design system, so it adds nothing to the initial stylesheet.
 */
import { useId, useState, type InputHTMLAttributes } from 'react';

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
  hint?: string;
  /** Show the strength meter (new passwords) */
  meter?: boolean;
  /** The rules to meet; the platform's: 8+ characters with an uppercase, a lowercase letter and a number */
  rules?: ReadonlyArray<{ label: string; test: (v: string) => boolean }>;
}

export const STAFF_PASSWORD_RULES = [
  { label: '8 or more characters', test: (v: string) => v.length >= 8 },
  { label: 'an uppercase letter', test: (v: string) => /[A-Z]/.test(v) },
  { label: 'a lowercase letter', test: (v: string) => /[a-z]/.test(v) },
  { label: 'a number', test: (v: string) => /\d/.test(v) },
];

const STRENGTH = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'] as const;
const TONE = ['bg-destructive', 'bg-destructive', 'bg-warning', 'bg-success', 'bg-success'] as const;

export function PasswordField({ label, hint, meter = false, rules = STAFF_PASSWORD_RULES, id, value, ...input }: Props) {
  const generated = useId();
  const inputId = id ?? generated;
  const [visible, setVisible] = useState(false);
  const text = typeof value === 'string' ? value : '';
  const met = rules.filter((r) => r.test(text)).length;
  const score = text ? Math.min(4, Math.round((met / rules.length) * 3) + (text.length >= 12 && met === rules.length ? 1 : 0)) : 0;
  const missing = rules.filter((r) => !r.test(text)).map((r) => r.label);
  const describedBy = [hint ? `${inputId}-hint` : null, meter ? `${inputId}-meter` : null].filter(Boolean).join(' ') || undefined;
  return (
    <div className="field">
      <label htmlFor={inputId}>{label}</label>
      <div className="relative">
        <input id={inputId} type={visible ? 'text' : 'password'} aria-describedby={describedBy} value={value} className="pr-20" {...input} />
        <button
          type="button"
          className="absolute right-1 top-1/2 min-h-[36px] -translate-y-1/2 rounded-control px-3 text-sm font-semibold text-muted-foreground hover:text-foreground"
          aria-pressed={visible}
          aria-controls={inputId}
          onClick={() => setVisible((v) => !v)}
        >
          {visible ? 'Hide' : 'Show'}
          <span className="visually-hidden"> password</span>
        </button>
      </div>
      {hint ? (
        <small id={`${inputId}-hint`} className="field-hint">
          {hint}
        </small>
      ) : null}
      {meter ? (
        <div id={`${inputId}-meter`} className="grid gap-1" aria-live="polite">
          <div className="grid grid-cols-4 gap-1" aria-hidden="true">
            {[1, 2, 3, 4].map((i) => (
              <span key={i} className={`h-1 rounded-full ${i <= score ? TONE[score] : 'bg-muted'}`} />
            ))}
          </div>
          {text ? (
            <small className="field-hint">
              Strength: {STRENGTH[score]}
              {missing.length ? `. Add ${missing.join(', ')}.` : '.'}
            </small>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
